<template>
  <el-dropdown
    v-if="stateOverridingAllowed"
    trigger="click"
    @visible-change="resetSearchAndSave"
  >
    <icon class="settings-icon" glyph="cog" size="20" />
    <el-dropdown-menu
      slot="dropdown"
      :style="dropdownCustomStyle"
      :class="{ labeled: labeled }"
    >
      <div class="settings-title">
        {{ title }}
        <el-input
          v-if="dynamicColumns"
          class="filter-input"
          :placeholder="$t('RestList_Column_Filter')"
          suffix-icon="el-icon-search"
          v-model="searchInput"
          size="small"
          @input="updateColumns"
        />
        <el-input
          v-if="filterable"
          class="filter-input"
          :placeholder="$t('RestList_Column_Search')"
          prefix-icon="el-icon-search"
          v-model="searchQuery"
          size="small"
          clearable
        />
      </div>
      <div class="columnSelection" v-if="groupColumns">
        <div
          class="ColumnsSection"
          v-for="(group, index) in Object.keys(groupedColumns).sort()"
          :key="index"
          v-if="Object.keys(groupedColumns[group]).length"
        >
          <span class="ColumnsSection--Title">
            {{ $t(`ColumnsSection_${group}`) }}
          </span>
          <el-checkbox
            v-for="(column, cIndex) in groupedColumns[group]"
            v-model="selectedColumns[cIndex]"
            :key="cIndex"
            @change="saveColumns"
          >
            {{ truncate(column.title) }}
          </el-checkbox>
        </div>
      </div>
      <div class="columnSelection" v-else>
        <template v-if="sortable">
          <draggable
            :list="filteredKeys"
            :disabled="!isDraggingAvailable"
            @change="saveColumns"
          >
            <div class="collection-item" v-for="key in filteredKeys">
              <span
                class="xp-icon-hamburger-menu"
                :class="{ 'non-visible': !isDraggingAvailable }"
              ></span>
              <div class="preference-name-block">
                <el-tooltip
                  v-if="!selectedColumns[key] && selectedColumnsLimitReached"
                  class="RestList--TooltipContent"
                  effect="light"
                  :content="selectLimitText"
                  placement="top"
                >
                  <el-checkbox
                    :data-qa="defaultValidatedColumns[key].qaTag"
                    :key="key"
                    :disabled="true"
                    class="disabled-without-opacity"
                  >
                    {{ defaultValidatedColumns[key].title }}
                  </el-checkbox>
                </el-tooltip>
                <el-checkbox
                  v-else
                  v-model="selectedColumns[key]"
                  :data-qa="defaultValidatedColumns[key].qaTag"
                  :key="key"
                  @change="saveColumns"
                  :disabled="disabledColumns"
                >
                  {{ defaultValidatedColumns[key].title }}
                </el-checkbox>
                <span class="column-label" v-if="labeled">
                  {{
                    defaultValidatedColumns[key].label
                      ? $t(defaultValidatedColumns[key].label)
                      : $t('RestForm_Settings_Basic')
                  }}
                </span>
              </div>
            </div>
          </draggable>
        </template>
        <template v-else>
          <el-checkbox
            v-for="(column, index) in defaultValidatedColumns"
            v-model="selectedColumns[index]"
            :data-qa="column.qaTag"
            :key="index"
            @change="saveColumns"
          >
            {{ column.title }}
          </el-checkbox>
        </template>
        <div class="hidden hiddenColumnSelection" v-if="showAttributes">
          <el-checkbox
            v-for="(column, index) in hiddenValidatedColumns"
            v-model="selectedColumns[index]"
            :key="index"
          >
            {{ truncate(column.title) }}
          </el-checkbox>
        </div>
        <expandable-control
          v-if="showHiddenColumnsDropdown"
          class="expandable-el-card"
          :open-text="$t('RestForm_Settings_Hidden_Attributes')"
          :close-text="$t('RestForm_Settings_Hidden_Attributes')"
          @toggle="toggleHiddenAttributes"
        />
      </div>
    </el-dropdown-menu>
  </el-dropdown>
</template>

<script>
import ExpandableControl from '@/modules/analytics/components/ExpandableControl'
import draggable from 'vuedraggable'
import _ from 'lodash'

export default {
  name: 'RestListColumnSettings',
  components: { ExpandableControl, draggable },
  data() {
    return {
      selectedColumns: {},
      dropdownCustomStyle: {
        padding: '0px',
        overflowY: 'auto',
        overflowX: 'hidden',
        maxHeight: '100%'
      },
      showAttributes: false,
      searchInput: '',
      searchQuery: '',
      sequence: []
    }
  },

  props: {
    title: {
      required: true,
      type: String
    },

    columns: {
      type: Object,
      required: true
    },

    storageKey: {
      required: true,
      type: String
    },

    value: {
      type: Object
    },

    dynamicColumns: {
      type: Boolean,
      default: false
    },

    groupColumns: {
      type: Boolean,
      default: false
    },

    sortable: {
      type: Boolean,
      default: false
    },

    filterable: {
      type: Boolean,
      default: false
    },

    labeled: {
      type: Boolean,
      default: false
    },

    disabledColumns: {
      type: Boolean,
      default: false
    },

    selectLimit: {
      type: Number,
      default: 0
    },

    predefinedColumns: {
      type: Object,
      default: () => ({})
    },

    stateOverridingAllowed: {
      type: Boolean,
      default: true
    }
  },

  mounted() {
    this.initSequence()
  },

  computed: {
    validatedColumns() {
      let validatedColumns = {}
      Object.keys(this.columns)
        .filter(key => {
          return this.columns[key].validation
        })
        .map(key => {
          validatedColumns[key] = this.columns[key]
        })
      return validatedColumns
    },

    defaultValidatedColumns() {
      let defaultValidatedColumns = {}
      Object.keys(this.validatedColumns)
        .filter(key => {
          return (
            !this.columns[key].hasOwnProperty('hidden') ||
            !this.columns[key].hidden
          )
        })
        .map(key => {
          defaultValidatedColumns[key] = this.columns[key]
        })
      return defaultValidatedColumns
    },

    hiddenValidatedColumns() {
      let hiddenValidatedColumns = {}
      Object.keys(this.validatedColumns)
        .filter(key => {
          return (
            this.columns[key].hasOwnProperty('hidden') &&
            this.columns[key].hidden
          )
        })
        .map(key => {
          hiddenValidatedColumns[key] = this.columns[key]
        })
      return hiddenValidatedColumns
    },

    showHiddenColumnsDropdown() {
      return Object.keys(this.hiddenValidatedColumns).length > 0
    },

    groupedColumns() {
      if (!this.groupColumns) {
        return {}
      }

      return Object.keys(this.columns).reduce((result, item) => {
        const group = this.columns[item].group || 'common'

        if (!Object.hasOwn(result, group)) {
          result[group] = {}
        }

        result[group][item] = this.columns[item]

        return result
      }, {})
    },

    isDraggingAvailable() {
      return this.searchQuery.length === 0 && !this.disabledColumns
    },

    filteredKeys() {
      if (this.searchQuery.length > 0) {
        const lookingFor = this.searchQuery.toLowerCase()

        return this.sequence
          .filter(k => {
            return (
              this.defaultValidatedColumns[k].title
                .toLowerCase()
                .indexOf(lookingFor) !== -1 ||
              (this.defaultValidatedColumns[k].hasOwnProperty('key') &&
                this.defaultValidatedColumns[k].key
                  .toLowerCase()
                  .indexOf(lookingFor) !== -1) ||
              k.toLowerCase().indexOf(lookingFor) !== -1 ||
              this.selectedColumns[k] === true
            )
          })
          .sort((a, b) => this.selectedColumns[b] - this.selectedColumns[a])
      } else {
        return this.sequence
      }
    },

    selectedColumnsCount() {
      let selectedCount = 0
      Object.keys(this.selectedColumns).forEach(key => {
        if (this.selectedColumns[key]) {
          selectedCount++
        }
      })

      return selectedCount
    },

    selectedColumnsLimitReached() {
      return (
        this.selectLimit > 0 && this.selectedColumnsCount >= this.selectLimit
      )
    },

    selectLimitText() {
      return this.$t('RestList_Column_MaximumLimit', {
        count: this.selectLimit
      })
    }
  },

  methods: {
    truncate(title) {
      let max = 25
      if (title.length > max) {
        return `${title.substr(0, max)}..`
      }
      return title
    },

    resetSearchAndSave(open) {
      this.searchQuery = ''
      if (!open) {
        this.saveColumns(false)
      }
    },

    saveColumns(val) {
      if (this.stateOverridingAllowed) {
        localStorage.setItem(
          this.storageKey,
          JSON.stringify(this.selectedColumns)
        )
      }
    },

    updateColumns(searchInputValue) {
      this.$emit('update-columns', searchInputValue)
    },

    toggleHiddenAttributes(toggleState) {
      this.showAttributes = toggleState
    },

    getColumns() {
      if (Object.keys(this.predefinedColumns).length > 0) {
        return this.predefinedColumns
      }

      return JSON.parse(localStorage.getItem(this.storageKey))
    },

    initSequence() {
      let savedColumns = this.getColumns()

      Object.keys(
        savedColumns
          ? (savedColumns = this.alignObjectsPreserveOrder(
              savedColumns,
              this.convertToBooleanMap(this.defaultValidatedColumns)
            ))
          : this.columns
      )
        .filter(key => {
          return this.columns[key].validation
        })
        .map(key => {
          if (savedColumns && savedColumns[key] !== undefined) {
            this.$set(this.selectedColumns, key, savedColumns[key])
          } else {
            this.$set(this.selectedColumns, key, this.columns[key].defaultValue)
          }
        })

      if (savedColumns && this.sortable) {
        this.sequence = Object.keys(savedColumns)
      } else {
        this.sequence = Object.keys(this.defaultValidatedColumns)
      }
    },

    alignObjectsPreserveOrder(object1, object2) {
      const result = {}

      _.forEach(_.keys(object1), key => {
        if (_.has(object2, key)) result[key] = object1[key]
      })

      _.forEach(_.keys(object2), key => {
        if (!_.has(result, key)) result[key] = false
      })

      return result
    },

    convertToBooleanMap(originalObject) {
      return _.mapValues(originalObject, value => value.defaultValue || false)
    }
  },

  watch: {
    selectedColumns: {
      deep: true,
      handler(selectedColumns) {
        this.$emit('input', selectedColumns)
        this.$emit('updated')
      }
    },
    sequence(sorted) {
      let sortedSelectedColumns = {}
      sorted.forEach(c => {
        sortedSelectedColumns[c] = this.selectedColumns[c]
      })
      this.selectedColumns = sortedSelectedColumns
    }
  }
}
</script>

<style scoped>
.columnSelection {
  display: flex;
  flex-direction: column;
  padding: 10px;
  font-size: 14px;
}

.hiddenColumnSelection {
  display: flex;
  flex-direction: column;
  font-size: 14px;
  margin-top: 5px;
  padding-top: 5px;
  border-top: 1px solid #dbdee7;
}

.settings-icon {
  cursor: pointer;
}

.settings-title {
  background-color: #fafafa;
  padding: 10px;
  color: #333333;
  font-weight: 600;
  font-size: 14px;
  border-bottom: 1px solid #eaebec;
}

>>> .el-checkbox {
  margin-top: 5px;
  display: flex;
}

>>> .el-checkbox__input .el-checkbox__inner {
  border-radius: 4px;
  border: 1px solid #dbdee7;
}

>>> .el-checkbox__input.is-checked .el-checkbox__inner {
  border-radius: 4px;
  background-color: #1d87c8;
  border-color: #1d87c8;
  color: #333333;
}

.filter-input {
  margin-top: 5px;
}

.expandable-control {
  margin: 0 !important;
}

.ColumnsSection {
  display: flex;
  flex-direction: column;
  font-size: 14px;
}

.ColumnsSection .ColumnsSection--Title {
  padding: 10px;
  color: #333333;
  font-weight: 600;
  font-size: 14px;
}

.column-label {
  color: #c2c2c3;
}

.collection-item {
  display: flex;
  align-items: center;
  min-height: 40px !important;
}

.xp-icon-hamburger-menu {
  display: flex;
  align-content: center;
}

.preference-name-block {
  width: calc(100% - 14px);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.preference-name-block >>> .el-checkbox {
  margin-bottom: 0 !important;
}

.el-dropdown-menu.labeled {
  margin-top: 280px !important;
  width: 330px !important;
  max-height: calc(100% - 280px) !important;
}

.non-visible {
  visibility: hidden;
}

>>> .el-checkbox__input {
  line-height: 1.7;
}

>>> .el-checkbox__label {
  max-width: 154px;
  display: inline-block;
  white-space: nowrap;
  overflow: hidden !important;
  text-overflow: ellipsis;
}

.disabled-without-opacity >>> .el-checkbox__label {
  color: #606266 !important;
}

.disabled-without-opacity
  >>> .el-checkbox__input.is-disabled
  .el-checkbox__inner {
  background-color: #fff;
}
</style>



// WEBPACK FOOTER //
// src/common/components/rest/RestListColumnSettings.vue